import { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Quill } from 'react-quill';
import 'quill/dist/quill.snow.css';
import logger from 'utils/logger';
import { EditorCont } from './Styles';

const propTypes = {
  className: PropTypes.string,
  placeholder: PropTypes.string,
  defaultValue: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  quoteAttachment: PropTypes.object,
  refreshQuote: PropTypes.number,
  commentEditorRef: PropTypes.shape({
    // Quill object
    current: PropTypes.object,
  }),
};

const log = logger.createLogger('CommentTextEditor');

const CommentTextEditor = ({
  className = undefined,
  placeholder = undefined,
  defaultValue = undefined,
  // we're not really feeding new value to quill instance on each render because it's too
  // expensive, but we're still accepting 'value' prop as alias for defaultValue because
  // other components like <Form.Field> feed their children with data via the 'value' prop
  value: alsoDefaultValue,
  onChange = () => {},
  quoteAttachment,
  refreshQuote,
  commentEditorRef,
}) => {
  const $editorContRef = useRef();
  const $editorRef = useRef();
  const quillRef = useRef(null);
  const initialValueRef = useRef(defaultValue || alsoDefaultValue || '');

  useEffect(() => {
    const handleContentsChange = () => {
      onChange(getHTMLValue());
    };

    const getHTMLValue = () =>
      $editorContRef.current?.querySelector('.ql-editor')?.innerHTML;

    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      if (!$editorRef.current || quillRef.current) {
        return;
      }

      quillRef.current = new Quill($editorRef.current, {
        placeholder,
        ...quillConfig,
      });
      commentEditorRef.current = quillRef.current;

      const insertInitialValue = () => {
        quillRef.current.clipboard.dangerouslyPasteHTML(
          0,
          initialValueRef.current
        );
        quillRef.current.blur();
      };

      insertInitialValue();
      quillRef.current.on('text-change', handleContentsChange);

      // Trigger quote insertion if refreshQuote is already set
      if (refreshQuote > 0 && quoteAttachment) {
        insertQuote();
      }
    }, 0);

    return () => {
      clearTimeout(timer);
      if (quillRef.current) {
        quillRef.current.off('text-change', handleContentsChange);
        quillRef.current = null;
      }
    };
  }, []);

  // Helper function to insert quote
  const insertQuote = () => {
    if (!commentEditorRef.current || !quoteAttachment) return;

    let length = commentEditorRef.current.getLength();
    // The editor always has at least a new line character.
    // use `length - 1` if you do not want the new line;

    // Insert embed normally
    commentEditorRef.current.insertEmbed(length - 1, 'blockquote', '');
    commentEditorRef.current.insertText(length - 1, quoteAttachment.title);

    // Add id to the blockquote element
    const editorElement = commentEditorRef.current.root;
    const blockquotes = editorElement.querySelectorAll('blockquote');
    const lastBlockquote = blockquotes[blockquotes.length - 1];
    if (lastBlockquote) {
      //! Adding the data-id attribute to the blockquote element
      lastBlockquote.setAttribute('data-id', quoteAttachment.id);
    }

    commentEditorRef.current.setSelection(commentEditorRef.current.getLength());
    commentEditorRef.current.focus();
  };

  // The commonEditorRef is null at the first instance
  useEffect(() => {
    if (refreshQuote === 0 || !commentEditorRef.current) return;

    insertQuote();
  }, [refreshQuote]);

  return (
    <EditorCont className={className} ref={$editorContRef}>
      <div ref={$editorRef} />
    </EditorCont>
  );
};

const quillConfig = {
  theme: 'snow',
  modules: {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ header: [1, 2, 3, 4, false] }],
      ['clean'],
    ],
  },
};

CommentTextEditor.propTypes = propTypes;

export default CommentTextEditor;
